
const libPictProvider = require('pict-provider');

const libCatalog = require('../access/Permission-Catalog.js');
const libMatrixBuilder = require('../access/Access-Matrix-Builder.js');

// PermissionsEnforcement modes (lib/permissions-control PermissionsControl). 'Default' = not enforced
// (grants are inert here); the rest bite. Ordered least → most restrictive for the picker.
const ENFORCEMENT_MODES = [ 'Default', 'Level', 'Mine', 'Hybrid', 'Strict' ];

/**
 * PermissionMatrix — the role/permission matrix tab of the Access workspace.
 *
 * Reads a role's (or permission group's) saved grants (RolePermission Allowed=1) and denies (Allowed=0) for
 * the workspace's current Context+IDContext, and turns them into the tri-state grid (rows = catalog entities
 * grouped by kind, cols = the 10 actions) via the pure Access-Matrix-Builder. Phase 1 is read-only; phase 2
 * adds the Grant/Deny/clear write path. The subject + context come from the AccessWorkspace provider (the
 * single owner); this provider only owns the matrix slice of AppData (AppData.AccessWorkspace.Matrix).
 */
class PermissionMatrixProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;

		// The active catalog source: the STATIC entity catalog (default) or a DYNAMIC reports/forms catalog
		// built from the live ReportNamedInstance set. Built lazily; the reports source is rebuilt when the
		// context's report set changes. See Access-Matrix-Builder for the source interface.
		this._staticSource = null;
		this._reportSource = null;
	}

	get workspace()
	{
		return this.pict.providers.AccessWorkspace;
	}

	/**
	 * The catalog source for the current mode. Reports mode falls back to an empty reports source until the
	 * ReportNamedInstance set has loaded (so derive never throws mid-load).
	 * @return {Object}
	 */
	get activeSource()
	{
		if (this.matrix.CatalogMode === 'reports')
		{
			return this._reportSource || libMatrixBuilder.reportCatalogSource([], {});
		}
		if (!this._staticSource)
		{
			this._staticSource = libMatrixBuilder.staticCatalogSource();
		}
		return this._staticSource;
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	/**
	 * The matrix slice of the shared workspace AppData (lazily created).
	 * @return {Object}
	 */
	get matrix()
	{
		const tmpWorkspaceState = this.workspace.state;
		if (!tmpWorkspaceState.Matrix)
		{
			tmpWorkspaceState.Matrix =
			{
				RoleID: 0, Context: '', IDContext: 0, Loading: false, Loaded: false, Error: '', Search: '',
				GrantHashes: [], DenyHashes: [], GrantByHash: {}, DenyByHash: {}, Edits: {},
				Actions: [], Sections: [], GrantCount: 0, DenyCount: 0,
				Editable: false, Saving: false, EditCount: 0, SaveNotice: '', SaveNoticeClass: '',
				LoadingSlot: [], ErrorSlot: [], GridSlot: [], NoMatchSlot: [],
				AllHeadSlot: [], EditHintSlot: [], SaveBarSlot: [], SaveNoticeSlot: [],
				// Reports/forms mode: the switchable catalog axis + the PermissionsEnforcement layer.
				CatalogMode: 'entities', ReportsKey: '', ReportsLoading: false,
				EnforcementDocument: {}, EnforcementConfig: null, EnforcementCustomerIDs: null, EnforcementLoadError: '',
				ModeSlot: [], EnforcementHeadSlot: [], EnforcementBannerSlot: [],
			};
		}
		return tmpWorkspaceState.Matrix;
	}

	// --- REST helpers ---

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_postJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.postJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_deleteJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			// The rest client exposes deleteEntity(entity, id) (there is no raw deleteJSON).
			const tmpMatch = /^([A-Za-z][A-Za-z0-9]*)\/(\d+)$/.exec(String(pURL));
			if (tmpMatch)
			{
				this.restClient.deleteEntity(tmpMatch[1], tmpMatch[2], (pError, pBody) => pError ? reject(pError) : resolve(pBody));
				return;
			}
			const tmpBase = this.restClient.serverURL || '/1.0/';
			fetch(`${tmpBase}${pURL}`, { method: 'DELETE', credentials: 'include' })
				.then((pResponse) =>
				{
					if (!pResponse.ok) { reject(new Error(`HTTP ${pResponse.status}`)); return; }
					pResponse.text().then((pText) => resolve(pText ? JSON.parse(pText) : {})).catch(() => resolve({}));
				})
				.catch(reject);
		});
	}

	/**
	 * Read an entire filtered set (Count then a single sized page).
	 * @param {String} pEntitySet
	 * @param {String} pFilter
	 * @param {Number} [pCap]
	 * @return {Promise<Array>}
	 */
	async _getFilteredAll(pEntitySet, pFilter, pCap)
	{
		const tmpCap = pCap || 5000;
		let tmpTotal = tmpCap;
		try
		{
			const tmpCount = await this._getJSON(`${pEntitySet}/Count/FilteredTo/${pFilter}`);
			tmpTotal = Math.min((tmpCount && tmpCount.Count) || 0, tmpCap);
		}
		catch (pError)
		{
			tmpTotal = tmpCap;
		}
		if (!tmpTotal)
		{
			return [];
		}
		const tmpRows = await this._getJSON(`${pEntitySet}/FilteredTo/${pFilter}/0/${tmpTotal}`);
		return Array.isArray(tmpRows) ? tmpRows : [];
	}

	/**
	 * Fetch the RolePermission rows for a role+context at a given Allowed flag.
	 * @param {Number} pIDRole
	 * @param {String} pContext
	 * @param {Number} pIDContext
	 * @param {Number} pAllowed - 1 (grant) or 0 (deny)
	 * @return {Promise<Array>}
	 */
	_readPermissionRows(pIDRole, pContext, pIDContext, pAllowed)
	{
		const tmpFilter = `FBV~Context~EQ~${pContext}~FBV~IDContext~EQ~${pIDContext}~FBV~IDRole~EQ~${pIDRole}~FBV~Allowed~EQ~${pAllowed}`;
		return this._getFilteredAll('RolePermissions', tmpFilter, 5000);
	}

	/**
	 * Resolve the RolePermission STORAGE scope for the current role + workspace context. A role's direct grants
	 * live in the membership context (Customer -> CustomerMembership) keyed by the role's OWN customer (0 for a
	 * global role) — not the entity 'Customer'/<userCustomer> scope, where the resolver only cascades them in.
	 * @return {{ Context: String, IDContext: Number }}
	 */
	_scope()
	{
		const tmpWorkspaceState = this.workspace.state;
		const tmpRole = tmpWorkspaceState.SubjectRecord || {};
		if (tmpWorkspaceState.Context === 'Customer')
		{
			const tmpRoleCustomer = Number(tmpRole.IDCustomer || 0);
			// GLOBAL roles store grants in the membership context (CustomerMembership/0); CUSTOMER-SPECIFIC
			// roles store them in the entity context keyed by their own customer (Customer/<IDCustomer>).
			return (tmpRoleCustomer === 0)
				? { Context: 'CustomerMembership', IDContext: 0 }
				: { Context: 'Customer', IDContext: tmpRoleCustomer };
		}
		return { Context: libCatalog.contextToMembership(tmpWorkspaceState.Context), IDContext: Number(tmpWorkspaceState.IDContext) };
	}

	// --- Load ---

	/**
	 * Load the matrix for the workspace's current role + context. No-op for a user subject. Skips the fetch
	 * when the role + context already match what's loaded (unless pForce).
	 * @param {Boolean} [pForce]
	 */
	async loadAsync(pForce)
	{
		const tmpWorkspaceState = this.workspace.state;
		if (!tmpWorkspaceState.SubjectRecord || tmpWorkspaceState.SubjectKind === 'user')
		{
			return;
		}
		const tmpRoleID = Number(tmpWorkspaceState.SubjectID);
		const tmpScope = this._scope();
		const tmpContext = tmpScope.Context;
		const tmpIDContext = tmpScope.IDContext;
		const tmpMatrix = this.matrix;
		tmpMatrix.Editable = !!this.workspace.isRoleManager;

		const tmpUnchanged = tmpMatrix.Loaded && !pForce &&
			(tmpMatrix.RoleID === tmpRoleID) && (tmpMatrix.Context === tmpContext) && (tmpMatrix.IDContext === tmpIDContext);
		if (tmpUnchanged)
		{
			this._render();
			return;
		}

		tmpMatrix.RoleID = tmpRoleID;
		tmpMatrix.Context = tmpContext;
		tmpMatrix.IDContext = tmpIDContext;
		tmpMatrix.Loaded = false;
		tmpMatrix.Error = '';
		tmpMatrix.Edits = {};

		if (tmpWorkspaceState.Context !== 'Customer' && !tmpIDContext)
		{
			tmpMatrix.Loading = false;
			tmpMatrix.Error = `Enter a ${tmpWorkspaceState.Context} ID above to scope the permissions.`;
			this._render();
			return;
		}

		tmpMatrix.Loading = true;
		this._render();
		try
		{
			const tmpRows = await Promise.all(
				[
					this._readPermissionRows(tmpRoleID, tmpContext, tmpIDContext, 1),
					this._readPermissionRows(tmpRoleID, tmpContext, tmpIDContext, 0),
				]);
			const tmpGrant = libMatrixBuilder.dedupRolePermissionRows(tmpRows[0]);
			const tmpDeny = libMatrixBuilder.dedupRolePermissionRows(tmpRows[1]);
			tmpMatrix.GrantByHash = tmpGrant.byHash;
			tmpMatrix.GrantHashes = tmpGrant.hashes;
			tmpMatrix.DenyByHash = tmpDeny.byHash;
			tmpMatrix.DenyHashes = tmpDeny.hashes;
			// The role's grant/deny rows are the same set regardless of axis; reports mode also needs the
			// context's ReportNamedInstance set + enforcement config to know which report ROWS to render.
			if (tmpMatrix.CatalogMode === 'reports')
			{
				await this._ensureReportsAsync();
			}
			tmpMatrix.Loaded = true;
		}
		catch (pError)
		{
			tmpMatrix.Error = `Could not load permissions: ${pError && pError.message ? pError.message : pError}`;
		}
		tmpMatrix.Loading = false;
		this._render();
	}

	setSearch(pValue)
	{
		this.matrix.Search = pValue || '';
		this._render();
	}

	// --- Reports & Forms mode (dynamic ReportNamedInstance catalog + enforcement) ---

	/**
	 * Switch the matrix axis between the static entity catalog and the dynamic reports/forms catalog.
	 * @param {String} pMode - 'entities' | 'reports'
	 */
	setCatalogMode(pMode)
	{
		const tmpMode = (pMode === 'reports') ? 'reports' : 'entities';
		const tmpMatrix = this.matrix;
		if (tmpMatrix.CatalogMode === tmpMode)
		{
			return;
		}
		tmpMatrix.CatalogMode = tmpMode;
		tmpMatrix.Edits = {};   // pending edits don't carry across axes
		tmpMatrix.SaveNotice = '';
		tmpMatrix.Search = '';
		if (tmpMode === 'reports')
		{
			this._switchToReportsAsync();
		}
		else
		{
			this._render();
		}
	}

	async _switchToReportsAsync()
	{
		const tmpMatrix = this.matrix;
		tmpMatrix.ReportsLoading = true;
		this._render();
		try
		{
			await this._ensureReportsAsync();
		}
		catch (pError)
		{
			tmpMatrix.Error = `Could not load reports & forms: ${pError && pError.message ? pError.message : pError}`;
		}
		tmpMatrix.ReportsLoading = false;
		this._render();
	}

	/**
	 * Ensure the reports/forms catalog source + enforcement config are loaded for the current context.
	 * Keyed by Context:IDContext so switching role (same context) reuses the report set; changing context
	 * refetches. No render (callers render).
	 */
	async _ensureReportsAsync()
	{
		const tmpMatrix = this.matrix;
		const tmpCtx = this.workspace.state.Context;
		const tmpCtxID = Number(this.workspace.state.IDContext) || 0;
		const tmpKey = `${tmpCtx}:${tmpCtxID}`;
		if (tmpMatrix.ReportsKey === tmpKey && this._reportSource)
		{
			return;
		}
		const tmpRNIs = await this._loadReportNamedInstancesAsync(tmpCtx, tmpCtxID);
		const tmpStatuses = await this._loadWorkflowStatusesAsync(tmpCtx, tmpCtxID, tmpRNIs);
		this._reportSource = libMatrixBuilder.reportCatalogSource(tmpRNIs, tmpStatuses);
		await this._loadEnforcementAsync(tmpCtx, tmpCtxID);
		tmpMatrix.ReportsKey = tmpKey;
	}

	/**
	 * The reports/forms available in the current context — by project when a project is scoped, else the
	 * admin's customer (mirrors the home app's ReportNamedInstancesByProject / By/IDCustomer enumeration).
	 * @return {Promise<Array>}
	 */
	async _loadReportNamedInstancesAsync(pContext, pIDContext)
	{
		const tmpCustomer = this.workspace.idCustomer;
		const tmpURL = (pContext === 'Project' && pIDContext > 0)
			? `ReportNamedInstancesByProject/${pIDContext}/0/1000`
			: `ReportNamedInstances/By/IDCustomer/${tmpCustomer}/0/1000`;
		const tmpRows = await this._getJSON(tmpURL);
		return Array.isArray(tmpRows) ? tmpRows : [];
	}

	/**
	 * Per-report approval-workflow states → the `View-<Status>` action columns. Best-effort: a report only
	 * gets status columns when it has an approval-workflow binding (cleaner than the home app's always-four).
	 * @return {Promise<Object>} { '<RNI.Hash>': ['Draft','Approved', ...] }
	 */
	async _loadWorkflowStatusesAsync(pContext, pIDContext, pRNIs)
	{
		const tmpStatuses = {};
		try
		{
			const tmpCtx = (pContext === 'Project' || pContext === 'Contract') ? pContext : 'Customer';
			const tmpCtxID = (tmpCtx === 'Customer') ? this.workspace.idCustomer : pIDContext;
			const tmpBindings = await this._getJSON(`Configuration/For/ApprovalWorkflowBindings/In/${tmpCtx}/${tmpCtxID}`);
			const tmpWorkflows = await this._getJSON(`Configuration/For/ApprovalWorkflow/In/${tmpCtx}/${tmpCtxID}`);
			const tmpDocBindings = (tmpBindings && tmpBindings.Document) || {};
			(pRNIs || []).forEach((pRNI) =>
			{
				const tmpWFHash = tmpDocBindings[pRNI.Hash];
				const tmpWF = tmpWFHash && tmpWorkflows && tmpWorkflows[tmpWFHash];
				if (tmpWF && Array.isArray(tmpWF.States))
				{
					tmpStatuses[pRNI.Hash] = tmpWF.States.map((pState) => pState.Status).filter(Boolean);
				}
			});
		}
		catch (pError)
		{
			// Best-effort: no approval-workflow status columns if the config isn't available.
		}
		return tmpStatuses;
	}

	/**
	 * The scope the PermissionsEnforcement config is read/written at for the current context.
	 * @return {{ Scope: String, ScopeID: Number }}
	 */
	_enforcementScope(pContext, pIDContext)
	{
		if (pContext === 'Project' && pIDContext > 0)
		{
			return { Scope: 'Project', ScopeID: pIDContext };
		}
		if (pContext === 'Contract' && pIDContext > 0)
		{
			return { Scope: 'Contract', ScopeID: pIDContext };
		}
		return { Scope: 'Customer', ScopeID: this.workspace.idCustomer };
	}

	/**
	 * Read the PermissionsEnforcement config for the context: the per-report modes (NamespaceValue
	 * `EnforcementTypes` → Document.<RNI.Hash>.Actions) + the global customer-enrollment gate
	 * (NamespaceValue `Configuration` → CustomerIDs). The gate is best-effort. A failed modes read is remembered in
	 * EnforcementLoadError: a mode change writes the whole EnforcementTypes blob back, so writing it from an unknown
	 * starting point would wipe every other stanza at that scope (e.g. LADOTD's Entity.Sample.Delete).
	 */
	async _loadEnforcementAsync(pContext, pIDContext)
	{
		const tmpMatrix = this.matrix;
		tmpMatrix.EnforcementDocument = {};
		tmpMatrix.EnforcementConfig = null;
		tmpMatrix.EnforcementCustomerIDs = null;
		tmpMatrix.EnforcementLoadError = '';
		const tmpLevel = this._enforcementScope(pContext, pIDContext);
		try
		{
			const tmpTypes = await this._postJSON('Configuration/Get',
				{ LevelContext: tmpLevel.Scope, LevelContextID: tmpLevel.ScopeID, NamespaceKey: 'PermissionsEnforcement', NamespaceValue: 'EnforcementTypes' });
			const tmpConfig = this._unwrapConfig(tmpTypes);
			tmpMatrix.EnforcementConfig = tmpConfig;
			tmpMatrix.EnforcementDocument = tmpConfig.Document || {};
		}
		catch (pError)
		{
			// Modes unknown: display reads as Default, and mode changes are refused until a reload succeeds.
			tmpMatrix.EnforcementLoadError = `The current enforcement settings could not be read, so they can't be changed: a change would overwrite them. Reload and try again. (${pError && pError.message ? pError.message : pError})`;
		}
		try
		{
			const tmpGate = await this._postJSON('Configuration/Get',
				{ LevelContext: 'Global', LevelContextID: 0, NamespaceKey: 'PermissionsEnforcement', NamespaceValue: 'Configuration' });
			const tmpGateConfig = this._unwrapConfig(tmpGate);
			// Only claim (non-)enrollment when there is an explicit CustomerIDs list; an empty/absent gate means
			// unknown (null → no banner) rather than "nobody is enrolled".
			tmpMatrix.EnforcementCustomerIDs = Array.isArray(tmpGateConfig.CustomerIDs) ? tmpGateConfig.CustomerIDs : null;
		}
		catch (pError)
		{
			// enrollment gate unknown → suppress the banner rather than mislead
		}
	}

	/**
	 * Configuration/Get returns EITHER { Config, LevelControlRecords } OR the raw config object depending on
	 * the namespace (mirrors ConfigViewer._getConfigAt). Normalize to the config object.
	 * @return {Object}
	 */
	_unwrapConfig(pResult)
	{
		if (pResult && pResult.Config !== undefined)
		{
			return pResult.Config || {};
		}
		return pResult || {};
	}

	/**
	 * The effective enforcement mode for a report action, walking per-report → per-report default → global
	 * default-scope → 'Default'. The Document map is keyed by the raw RNI hash (no `DocumentType-` prefix).
	 * @return {String} Default|Level|Mine|Hybrid|Strict
	 */
	_enforcementModeFor(pRNIHash, pAction)
	{
		const tmpDoc = this.matrix.EnforcementDocument || {};
		const tmpPer = tmpDoc[pRNIHash] && tmpDoc[pRNIHash].Actions;
		const tmpDefault = tmpDoc.DefaultScope && tmpDoc.DefaultScope.Actions;
		return (tmpPer && (tmpPer[pAction] || tmpPer.DefaultAction))
			|| (tmpDefault && (tmpDefault[pAction] || tmpDefault.DefaultAction))
			|| 'Default';
	}

	/**
	 * Set a report's enforcement mode (governs all its actions via Actions.DefaultAction), writing the
	 * PermissionsEnforcement `EnforcementTypes` config at the context scope, then re-reading. Role-manager
	 * gated. The server diffs vs the parent scope (arrays replace, equal values drop).
	 * @param {String} pRNIHash - the raw RNI hash (Document map key)
	 * @param {String} pMode
	 */
	async setEnforcementModeAsync(pRNIHash, pMode)
	{
		const tmpMatrix = this.matrix;
		if (!tmpMatrix.Editable || tmpMatrix.Saving)
		{
			return;
		}
		if (tmpMatrix.EnforcementLoadError)
		{
			tmpMatrix.SaveNotice = tmpMatrix.EnforcementLoadError;
			tmpMatrix.SaveNoticeClass = 'is-warning';
			this._render();
			return;
		}
		const tmpCtx = this.workspace.state.Context;
		const tmpCtxID = Number(this.workspace.state.IDContext) || 0;
		const tmpLevel = this._enforcementScope(tmpCtx, tmpCtxID);
		const tmpConfig = tmpMatrix.EnforcementConfig ? JSON.parse(JSON.stringify(tmpMatrix.EnforcementConfig)) : {};
		if (!tmpConfig.Document) { tmpConfig.Document = {}; }
		if (!tmpConfig.Document[pRNIHash]) { tmpConfig.Document[pRNIHash] = {}; }
		if (!tmpConfig.Document[pRNIHash].Actions) { tmpConfig.Document[pRNIHash].Actions = {}; }
		tmpConfig.Document[pRNIHash].Actions.DefaultAction = pMode;
		tmpMatrix.Saving = true;
		this._render();
		try
		{
			await this._postJSON(`Configuration/Put/PermissionsEnforcement/EnforcementTypes/${tmpLevel.Scope}/${tmpLevel.ScopeID}`, { Config: tmpConfig });
			await this._loadEnforcementAsync(tmpCtx, tmpCtxID);
			tmpMatrix.SaveNotice = `Enforcement for this report set to ${pMode}.`;
			tmpMatrix.SaveNoticeClass = 'is-success is-light';
		}
		catch (pError)
		{
			tmpMatrix.SaveNotice = `Could not set enforcement: ${pError && pError.message ? pError.message : pError}`;
			tmpMatrix.SaveNoticeClass = 'is-warning';
		}
		tmpMatrix.Saving = false;
		this._render();
	}

	// --- Write (grant / deny / clear) ---

	/**
	 * Cycle a single cell (none -> on -> off -> none). No-op when not editable or the cell is unavailable.
	 * @param {String} pHash
	 */
	toggleCell(pHash)
	{
		const tmpMatrix = this.matrix;
		if (!tmpMatrix.Editable)
		{
			return;
		}
		const tmpParsed = libCatalog.parseHash(pHash);
		if (!this.activeSource.hasAction(tmpParsed.Entity, tmpParsed.Action))
		{
			return;
		}
		tmpMatrix.Edits = libMatrixBuilder.toggleCell(pHash, tmpMatrix.GrantHashes, tmpMatrix.DenyHashes, tmpMatrix.Edits);
		this._render();
	}

	/**
	 * Cycle an entire row via its "All" control, by the row's current meta-state. No-op when not editable.
	 * @param {String} pKey - the row key (an entity name, or `DocumentType-<RNI.Hash>`)
	 */
	toggleAll(pKey)
	{
		const tmpMatrix = this.matrix;
		if (!tmpMatrix.Editable)
		{
			return;
		}
		const tmpSource = this.activeSource;
		const tmpCells = tmpSource.actionsForKey(pKey).map((pAction) =>
			(
				{
					Available: true,
					State: libMatrixBuilder.effectiveState(tmpSource.buildHash(pKey, pAction), tmpMatrix.GrantHashes, tmpMatrix.DenyHashes, tmpMatrix.Edits),
				}));
		const tmpNext = libMatrixBuilder.cycleState(libMatrixBuilder.metaState(tmpCells));
		tmpMatrix.Edits = libMatrixBuilder.applyAllToggle(pKey, tmpNext, tmpMatrix.GrantHashes, tmpMatrix.DenyHashes, tmpMatrix.Edits, tmpSource);
		this._render();
	}

	discard()
	{
		this.matrix.Edits = {};
		this.matrix.SaveNotice = '';
		this._render();
	}

	dismissNotice()
	{
		this.matrix.SaveNotice = '';
		this._render();
	}

	/**
	 * Persist the pending edits: each changed cell -> Grant (on) / Deny (off) / delete the RolePermission row
	 * (none), mirroring the home app's PermissionService.savePermissionForRole. Re-reads after to reflect the
	 * server's truth, and reports any rows the server rejected (e.g. a missing Permission-Grant -> 403).
	 */
	async saveAsync()
	{
		const tmpMatrix = this.matrix;
		if (!tmpMatrix.Editable || tmpMatrix.Saving)
		{
			return;
		}
		const tmpEdits = libMatrixBuilder.diffChanges(tmpMatrix.Edits);
		const tmpHashes = Object.keys(tmpEdits);
		if (!tmpHashes.length)
		{
			return;
		}
		tmpMatrix.Saving = true;
		tmpMatrix.SaveNotice = '';
		this._render();

		let tmpFailures = 0;
		for (const tmpHash of tmpHashes)
		{
			try
			{
				await this._saveCell(tmpHash, tmpEdits[tmpHash]);
			}
			catch (pError)
			{
				tmpFailures++;
			}
		}

		tmpMatrix.Saving = false;
		tmpMatrix.Edits = {};
		// Re-read so the grid reflects exactly what persisted.
		await this.loadAsync(true);

		if (tmpFailures > 0)
		{
			tmpMatrix.SaveNotice = `${tmpHashes.length - tmpFailures} change(s) saved; ${tmpFailures} rejected by the server (you may lack Permission-Grant in this context). The grid shows what actually saved.`;
			tmpMatrix.SaveNoticeClass = 'is-warning';
		}
		else
		{
			tmpMatrix.SaveNotice = `Saved ${tmpHashes.length} change(s).`;
			tmpMatrix.SaveNoticeClass = 'is-success is-light';
		}
		this._render();
	}

	/**
	 * Persist one cell. on -> Grant, off -> Deny (both GET endpoints that upsert the RolePermission row),
	 * none -> delete the existing RolePermission row (no-op if there isn't one).
	 * @param {String} pHash
	 * @param {String} pState - 'on' | 'off' | 'none'
	 * @return {Promise}
	 */
	async _saveCell(pHash, pState)
	{
		const tmpMatrix = this.matrix;
		const tmpRole = tmpMatrix.RoleID;
		const tmpContext = tmpMatrix.Context;
		const tmpIDContext = tmpMatrix.IDContext;
		if (pState === 'on')
		{
			return this._getJSON(`Permissions/Grant/${pHash}/To/Role/${tmpRole}/For/${tmpContext}/${tmpIDContext}`);
		}
		if (pState === 'off')
		{
			return this._getJSON(`Permissions/Deny/${pHash}/To/Role/${tmpRole}/For/${tmpContext}/${tmpIDContext}`);
		}
		const tmpRows = await this._getFilteredAll('RolePermissions',
			`FBV~Hash~EQ~${pHash}~FBV~Context~EQ~${tmpContext}~FBV~IDContext~EQ~${tmpIDContext}~FBV~IDRole~EQ~${tmpRole}`, 50);
		if (tmpRows.length && tmpRows[0].IDRolePermission)
		{
			return this._deleteJSON(`RolePermission/${tmpRows[0].IDRolePermission}`);
		}
		return null;
	}

	// --- Derive (pure; called from the workspace render cycle) ---

	/**
	 * Recompute the matrix render slots from the loaded grant/deny sets + the search filter. No network.
	 */
	deriveMatrix()
	{
		const tmpMatrix = this.matrix;
		const tmpSource = this.activeSource;
		const tmpIsReports = (tmpMatrix.CatalogMode === 'reports');
		tmpMatrix.LoadingSlot = (tmpMatrix.Loading || tmpMatrix.ReportsLoading) ? [ {} ] : [];
		tmpMatrix.ErrorSlot = tmpMatrix.Error ? [ { Error: tmpMatrix.Error } ] : [];
		tmpMatrix.Actions = tmpSource.actions().map((pAction) => ({ Action: pAction }));
		tmpMatrix.GrantCount = (tmpMatrix.GrantHashes || []).length;
		tmpMatrix.DenyCount = (tmpMatrix.DenyHashes || []).length;

		// The [ Entities | Reports & Forms ] axis toggle.
		tmpMatrix.ModeSlot = [ { EntitiesClass: tmpIsReports ? '' : 'is-link is-selected', ReportsClass: tmpIsReports ? 'is-link is-selected' : '' } ];
		tmpMatrix.EnforcementHeadSlot = tmpIsReports ? [ {} ] : [];
		tmpMatrix.EnforcementBannerSlot = this._enforcementBanner(tmpIsReports);

		const tmpEditable = !!tmpMatrix.Editable;
		tmpMatrix.EditCount = Object.keys(tmpMatrix.Edits || {}).length;
		tmpMatrix.AllHeadSlot = tmpEditable ? [ {} ] : [];
		tmpMatrix.EditHintSlot = tmpEditable ? [] : [ { Text: 'You can view these permissions but not change them here — editing needs a Role Manager role. The server is the final authority on every change.' } ];
		tmpMatrix.SaveBarSlot = (tmpEditable && tmpMatrix.EditCount > 0) ? [ { EditCount: tmpMatrix.EditCount, SaveDisabledAttr: tmpMatrix.Saving ? 'disabled' : '' } ] : [];
		tmpMatrix.SaveNoticeSlot = tmpMatrix.SaveNotice ? [ { Text: tmpMatrix.SaveNotice, Class: tmpMatrix.SaveNoticeClass || 'is-info' } ] : [];

		if (tmpMatrix.Loading || tmpMatrix.ReportsLoading || tmpMatrix.Error)
		{
			tmpMatrix.GridSlot = [];
			tmpMatrix.NoMatchSlot = [];
			return;
		}

		const tmpBuilt = libMatrixBuilder.buildRows(tmpMatrix.GrantHashes || [], tmpMatrix.DenyHashes || [], tmpMatrix.Edits || {}, tmpSource);
		const tmpSearch = (tmpMatrix.Search || '').toLowerCase().trim();
		const tmpSections = tmpSource.sections().map((pDef) => ({ Key: pDef.Key, Title: pDef.Title, Rows: [] }));
		const tmpByKind = {};
		tmpSections.forEach((pSection) => { tmpByKind[pSection.Key] = pSection; });

		tmpBuilt.rows.forEach((pRow) =>
		{
			if (tmpSearch && pRow.Entity.toLowerCase().indexOf(tmpSearch) < 0)
			{
				return;
			}
			(tmpByKind[pRow.Kind] || tmpSections[0]).Rows.push(this._decorateRow(pRow, tmpEditable, tmpIsReports));
		});

		tmpMatrix.Sections = tmpSections
			.filter((pSection) => pSection.Rows.length > 0)
			.map((pSection) => ({ Title: pSection.Title, RowCount: pSection.Rows.length, Rows: pSection.Rows }));
		tmpMatrix.GridSlot = tmpMatrix.Sections.length ? [ {} ] : [];
		tmpMatrix.NoMatchSlot = tmpMatrix.Sections.length ? [] : [ {} ];
	}

	/**
	 * The "enforcement is off for this customer" banner (reports mode only) — even a Strict grant is inert
	 * until the customer is enrolled in PermissionsEnforcement → Configuration.CustomerIDs.
	 * @return {Array}
	 */
	_enforcementBanner(pIsReports)
	{
		if (!pIsReports || this.matrix.EnforcementCustomerIDs === null)
		{
			return [];
		}
		const tmpCustomer = Number(this.workspace.idCustomer);
		const tmpEnrolled = (this.matrix.EnforcementCustomerIDs || []).map(Number).indexOf(tmpCustomer) > -1;
		return tmpEnrolled ? [] : [ { Text: 'Permissions enforcement is not enabled for this customer — report grants below will not restrict access until the customer is added to PermissionsEnforcement → Configuration.CustomerIDs.' } ];
	}

	_decorateRow(pRow, pEditable, pIsReports)
	{
		return {
			Entity: pRow.Entity,
			Key: pRow.Key,
			AllSlot: pEditable ? [ this._decorateAll(pRow) ] : [],
			Cells: pRow.Cells.map((pCell) => this._decorateCell(pCell, pEditable)),
			EnforceSlot: pIsReports ? [ this._decorateEnforcement(pRow, pEditable) ] : [],
		};
	}

	/**
	 * The per-row "All" control (editable mode): its icon/class/tooltip reflect the row's meta-state. Its
	 * `Entity` field carries the row KEY (the toggleAll argument), which for a report is `DocumentType-<Hash>`.
	 * @param {Object} pRow
	 * @return {Object}
	 */
	_decorateAll(pRow)
	{
		if (pRow.MetaState === 'on')
		{
			return { Entity: pRow.Key, CellClass: 'acw-cell acw-cell-clickable acw-cell-on', Icon: '<i class="fas fa-circle-check"></i>', Title: 'All granted — click to deny all' };
		}
		if (pRow.MetaState === 'off')
		{
			return { Entity: pRow.Key, CellClass: 'acw-cell acw-cell-clickable acw-cell-off', Icon: '<i class="fas fa-ban"></i>', Title: 'All denied — click to clear all' };
		}
		return { Entity: pRow.Key, CellClass: 'acw-cell acw-cell-clickable acw-cell-none', Icon: '<i class="far fa-circle acw-faint"></i>', Title: 'Click to grant all' };
	}

	/**
	 * The per-report enforcement cell (reports mode): the effective mode (a picker when editable, a badge
	 * otherwise) + an "inert" flag when the mode is Default so grants that don't bite are obvious.
	 * @param {Object} pRow
	 * @param {Boolean} pEditable
	 * @return {Object}
	 */
	_decorateEnforcement(pRow, pEditable)
	{
		const tmpRNIHash = pRow.Key.slice(libCatalog.REPORT_ENTITY_PREFIX.length);
		const tmpMode = this._enforcementModeFor(tmpRNIHash, 'View');
		const tmpInert = (tmpMode === 'Default');
		const tmpLoadError = this.matrix.EnforcementLoadError;
		if (tmpLoadError)
		{
			return { RNIHash: tmpRNIHash, Mode: 'Unknown', InertSlot: [], EditSlot: [], ReadSlot: [ { Mode: 'Unknown', BadgeClass: 'is-danger is-light', Title: tmpLoadError } ] };
		}
		return {
			RNIHash: tmpRNIHash,
			Mode: tmpMode,
			InertSlot: tmpInert ? [ {} ] : [],
			EditSlot: pEditable ? [ { RNIHash: tmpRNIHash, Options: ENFORCEMENT_MODES.map((pMode) => ({ Value: pMode, Label: pMode, SelectedAttr: (pMode === tmpMode) ? 'selected' : '' })) } ] : [],
			ReadSlot: pEditable ? [] : [ { Mode: tmpMode, BadgeClass: tmpInert ? 'is-light' : 'is-warning is-light', Title: '' } ],
		};
	}

	/**
	 * Turn a builder cell into its display (icon + class + tooltip). Unavailable cells render a faint dash;
	 * on = green check, off = red ban, none = blank (a faint target when editable). Editable + available cells
	 * carry the clickable affordance; a pending (unsaved) edit is ringed.
	 * @param {Object} pCell
	 * @param {Boolean} pEditable
	 * @return {Object}
	 */
	_decorateCell(pCell, pEditable)
	{
		if (!pCell.Available)
		{
			return { Hash: pCell.Hash, CellClass: 'acw-cell acw-cell-na', Icon: '<span class="acw-dash">–</span>', Title: '' };
		}
		const tmpClickable = pEditable ? ' acw-cell-clickable' : '';
		const tmpChanged = pCell.Changed ? ' acw-cell-changed' : '';
		if (pCell.State === 'on')
		{
			return { Hash: pCell.Hash, CellClass: `acw-cell acw-cell-on${tmpClickable}${tmpChanged}`, Icon: '<i class="fas fa-circle-check"></i>', Title: `Granted: ${pCell.Hash}` };
		}
		if (pCell.State === 'off')
		{
			return { Hash: pCell.Hash, CellClass: `acw-cell acw-cell-off${tmpClickable}${tmpChanged}`, Icon: '<i class="fas fa-ban"></i>', Title: `Denied: ${pCell.Hash}` };
		}
		return { Hash: pCell.Hash, CellClass: `acw-cell acw-cell-none${tmpClickable}${tmpChanged}`, Icon: pEditable ? '<i class="far fa-circle acw-faint"></i>' : '', Title: `No rule: ${pCell.Hash}` };
	}

	_render()
	{
		if (this.pict.views.AccessWorkspace)
		{
			this.pict.views.AccessWorkspace.render();
		}
	}
}

module.exports = PermissionMatrixProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'PermissionMatrix',
	AutoInitialize: true,
	AutoInitializeOrdinal: 1,
};
